'use client';

import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  forwardRef,
  HTMLAttributes,
} from 'react';
import { cn } from '../../utils/cn';
import { Button, type ButtonProps } from '../../atoms/Button';

// Context for managing tab state
interface TabsContextValue {
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  orientation: 'horizontal' | 'vertical';
  registerTab: (element: HTMLButtonElement | null) => void;
  tabs: (HTMLButtonElement | null)[];
}

const TabsContext = createContext<TabsContextValue | undefined>(undefined);

const useTabsContext = () => {
  const context = useContext(TabsContext);
  if (!context) {
    throw new Error('Tab components must be used within a Tabs component');
  }
  return context;
};

// Main Tabs container component
export interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  defaultIndex?: number;
  selectedIndex?: number;
  onChange?: (index: number) => void;
  orientation?: 'horizontal' | 'vertical';
  children: React.ReactNode;
}

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      className,
      defaultIndex = 0,
      selectedIndex: controlledSelectedIndex,
      onChange,
      orientation = 'horizontal',
      children,
      ...props
    },
    ref
  ) => {
    const [internalSelectedIndex, setInternalSelectedIndex] = useState(defaultIndex);
    const tabsRef = useRef<(HTMLButtonElement | null)[]>([]);

    // Support both controlled and uncontrolled modes
    const selectedIndex =
      controlledSelectedIndex !== undefined ? controlledSelectedIndex : internalSelectedIndex;

    const setSelectedIndex = (index: number) => {
      if (onChange) {
        onChange(index);
      } else {
        setInternalSelectedIndex(index);
      }
    };

    const registerTab = (element: HTMLButtonElement | null) => {
      if (element && !tabsRef.current.includes(element)) {
        tabsRef.current.push(element);
      }
    };

    const contextValue: TabsContextValue = {
      selectedIndex,
      setSelectedIndex,
      orientation,
      registerTab,
      tabs: tabsRef.current,
    };

    return (
      <TabsContext.Provider value={contextValue}>
        <div ref={ref} className={cn('w-full', className)} {...props}>
          {children}
        </div>
      </TabsContext.Provider>
    );
  }
);

Tabs.displayName = 'Tabs';

// TabList component - container for Tab buttons
export interface TabListProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const TabList = forwardRef<HTMLDivElement, TabListProps>(
  ({ className, children, ...props }, ref) => {
    const { orientation } = useTabsContext();

    return (
      <div
        ref={ref}
        role="tablist"
        aria-orientation={orientation}
        className={cn(
          'flex border-b-2 border-velux-light-grey',
          orientation === 'horizontal' ? 'flex-row gap-0' : 'flex-col gap-2',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

TabList.displayName = 'TabList';

// Individual Tab button
export interface TabProps extends Omit<ButtonProps, 'variant' | 'isActive' | 'isLoading'> {
  children: React.ReactNode;
}

export const Tab = forwardRef<HTMLButtonElement, TabProps>(
  ({ className, disabled, children, ...props }, ref) => {
    const { selectedIndex, setSelectedIndex, registerTab, tabs } = useTabsContext();
    const [tabIndex, setTabIndex] = useState<number>(-1);
    const internalRef = useRef<HTMLButtonElement | null>(null);

    // Combine refs
    useEffect(() => {
      if (internalRef.current) {
        registerTab(internalRef.current);
        const index = tabs.indexOf(internalRef.current);
        if (index !== -1) {
          setTabIndex(index);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isSelected = tabIndex === selectedIndex;

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!disabled && tabIndex !== -1) {
        setSelectedIndex(tabIndex);
      }
      // Call original onClick if provided
      if (props.onClick) {
        props.onClick(e);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (disabled) return;

      const currentIndex = tabs.indexOf(internalRef.current);
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;

      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          // Find next non-disabled tab
          for (let i = currentIndex + 1; i < tabs.length; i++) {
            if (tabs[i] && !tabs[i]?.disabled) {
              nextIndex = i;
              break;
            }
          }
          break;

        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          // Find previous non-disabled tab
          for (let i = currentIndex - 1; i >= 0; i--) {
            if (tabs[i] && !tabs[i]?.disabled) {
              nextIndex = i;
              break;
            }
          }
          break;

        case 'Home':
          e.preventDefault();
          // Find first non-disabled tab
          for (let i = 0; i < tabs.length; i++) {
            if (tabs[i] && !tabs[i]?.disabled) {
              nextIndex = i;
              break;
            }
          }
          break;

        case 'End':
          e.preventDefault();
          // Find last non-disabled tab
          for (let i = tabs.length - 1; i >= 0; i--) {
            if (tabs[i] && !tabs[i]?.disabled) {
              nextIndex = i;
              break;
            }
          }
          break;

        default:
          return;
      }

      if (nextIndex !== currentIndex && tabs[nextIndex]) {
        setSelectedIndex(nextIndex);
        tabs[nextIndex]?.focus();
      }
    };

    return (
      <Button
        ref={element => {
          internalRef.current = element;
          if (typeof ref === 'function') {
            ref(element);
          } else if (ref) {
            ref.current = element;
          }
        }}
        variant="underline"
        size="none"
        isActive={isSelected}
        disabled={disabled}
        role="tab"
        aria-selected={isSelected}
        tabIndex={isSelected ? 0 : -1}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={cn(
          'px-6 py-3 text-base font-medium',
          // Override Button's border color to use logo-red instead of red
          isSelected && '!border-b-velux-logo-red',
          // Add text colors that Button doesn't provide
          isSelected ? 'text-velux-black' : 'text-velux-grey hover:text-velux-black',
          className
        )}
        {...props}
      >
        {children}
      </Button>
    );
  }
);

Tab.displayName = 'Tab';

// TabPanels container
export interface TabPanelsProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const TabPanels = forwardRef<HTMLDivElement, TabPanelsProps>(
  ({ className, children, ...props }, ref) => {
    const { selectedIndex } = useTabsContext();

    const panels = React.Children.toArray(children);

    return (
      <div ref={ref} className={cn('mt-4', className)} {...props}>
        {panels.map((panel, index) => {
          if (React.isValidElement(panel)) {
            return React.cloneElement(panel, {
              ...panel.props,
              key: index,
              'data-index': index,
              'data-selected': index === selectedIndex,
            } as React.HTMLAttributes<HTMLDivElement> & {
              'data-index': number;
              'data-selected': boolean;
            });
          }
          return panel;
        })}
      </div>
    );
  }
);

TabPanels.displayName = 'TabPanels';

// Individual TabPanel
export interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  'data-index'?: number;
  'data-selected'?: boolean;
}

export const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(
  (
    { className, children, 'data-index': panelIndex, 'data-selected': isSelected, ...props },
    ref
  ) => {
    const { selectedIndex } = useTabsContext();

    // Use data-selected prop if provided by TabPanels, otherwise check selectedIndex
    const selected = isSelected !== undefined ? isSelected : panelIndex === selectedIndex;

    if (!selected) {
      return null;
    }

    return (
      <div
        ref={ref}
        role="tabpanel"
        aria-hidden={!selected}
        className={cn('focus:outline-none', className)}
        tabIndex={0}
        {...props}
      >
        {children}
      </div>
    );
  }
);

TabPanel.displayName = 'TabPanel';
